import { sortBy, take } from "lodash";
import { timestampToDate } from "./misc";

// The Brand picker floats up to TOP_COUNT brands, ranked by how many recent
// projects carry each one (Project.brands). The user's own projects (a sale
// assigned to them) rank first; their primary location's projects fill in
// after. Everything comes from the projects' mirrored sales, which the app
// already loads.
export const RECENT_PROJECT_COUNT = 50;
export const TOP_COUNT = 10;

// The RECENT_PROJECT_COUNT projects whose most recently sold, not-canceled
// sale passes `test`, newest first. A sale with no sold date counts as oldest.
function recentProjects(projects, test) {
  const matched = [];
  projects.forEach((project) => {
    let latest = null;
    Object.values(project.data.sales || {}).forEach((sale) => {
      if (!sale || sale.status === "canceled" || !test(sale)) return;
      const soldDate = timestampToDate(sale.soldDate);
      latest = Math.max(latest ?? 0, soldDate ? soldDate.getTime() : 0);
    });
    if (latest !== null) {
      matched.push({ project, latest });
    }
  });
  return take(
    sortBy(matched, ({ latest }) => -latest),
    RECENT_PROJECT_COUNT
  ).map(({ project }) => project);
}

// Brands by how many of `projects` carry them, then alphabetically.
function rankByProjectCount(projects) {
  const counts = {};
  projects.forEach((project) =>
    project.brands.forEach((brand) => {
      counts[brand] = (counts[brand] || 0) + 1;
    })
  );
  return Object.keys(counts).sort(
    (a, b) => counts[b] - counts[a] || a.localeCompare(b)
  );
}

/**
 * Every brand on the user's recent projects, most common first, followed by
 * the rest of their location's recent brands.
 *
 * @param {Project[]} projects
 * @param {string} userID
 * @param {string} [location] - the user's primary location (a LOCATIONS value)
 * @returns {string[]}
 */
export function rankBrands(projects, userID, location) {
  const ranked = userID
    ? rankByProjectCount(
        recentProjects(projects, (sale) =>
          (sale.assignedTo || []).includes(userID)
        )
      )
    : [];
  if (location) {
    rankByProjectCount(
      recentProjects(projects, (sale) => sale.location === location)
    ).forEach((brand) => {
      if (!ranked.includes(brand)) {
        ranked.push(brand);
      }
    });
  }
  return ranked;
}

/**
 * The first `topN` of `ranked` that are in `names`, followed by the rest of
 * `names` alphabetically.
 *
 * @param {string[]} names
 * @param {string[]} ranked - from rankBrands
 * @param {number} [topN=TOP_COUNT]
 * @returns {string[]} a new array
 */
export function sortByBrandRanking(names, ranked, topN = TOP_COUNT) {
  const top = take(
    ranked.filter((name) => names.includes(name)),
    topN
  );
  const rest = names
    .filter((name) => !top.includes(name))
    .sort((a, b) => a.localeCompare(b));
  return [...top, ...rest];
}
